/**
 * The duel ring (วงประลอง, docs/research/C0_PVP.md): a painted circle on the ground round a bout, so the edge of the
 * place a fighter is eased back from can be seen. Set by ui/duelHud.ts from the bout (`duelRing`); drawn under the figures.
 */
import { getSkill } from '@nine/shared';
import { UI } from '../labels';
import { globeBend } from '../globe';
import { ellipse } from '../drawKit';
import { registerRenderPass } from './registry';

export const duelRing = {
  on: false,
  x: 0,
  y: 0,
  r: 11,
  /** 0-1: a fighter is near the edge (the ring glows), and the clock the glow pulses on. */
  edge: 0,
  t: 0,
  /** The fighters' ids as the world holds them: the player's own body, and the other. -1: none. */
  me: -1,
  foe: -1,
  /** Seconds left of a counter stance and of a broken guard, [me, the other]. */
  stance: [0, 0] as [number, number],
  broken: [0, 0] as [number, number],
  /** Seconds of recovery left after a move, [me, the other] (duel v3 P1; passes/duelRecoveryTell.ts feeds it). */
  recov: [0, 0] as [number, number],
  /** The hub rival's id (or -1) and whether the player stands near: a marker over his head when no bout is on. */
  rival: -1,
  rivalNear: false,
};

if (import.meta.env.DEV) Object.assign(window, { __duelRing: duelRing });

registerRenderPass({
  id: 'duelRing',
  layer: 'ground',
  order: 225,
  draw({ g, iso, ox, oy }) {
    if (!duelRing.on) return;
    const [x, y] = iso.proj(duelRing.x, duelRing.y);
    const cx = x + ox;
    const cy = y + oy;
    const rx = duelRing.r * 22.6;
    const ry = duelRing.r * 11.3;
    const pulse = 0.5 + 0.5 * Math.sin(duelRing.t * 6);
    g.save();
    // The floor of the ring: a faint warm wash.
    g.fillStyle = 'rgba(214,172,96,.16)';
    ellipse(g, cx, cy, rx, ry);
    g.fill();
    // The edge: a double line, brighter and red when a fighter is near it.
    const warn = duelRing.edge;
    g.lineWidth = 5;
    g.strokeStyle = 'rgba(30,18,8,.6)';
    ellipse(g, cx, cy, rx + 1, ry + 0.5);
    g.stroke();
    g.lineWidth = 3;
    g.strokeStyle = warn > 0 ? `rgba(255,${Math.round(150 - 90 * warn * pulse)},90,${0.65 + 0.3 * pulse})` : 'rgba(255,226,160,.95)';
    ellipse(g, cx, cy, rx, ry);
    g.stroke();
    g.lineWidth = 1;
    g.strokeStyle = 'rgba(40,24,12,.7)';
    ellipse(g, cx, cy, rx - 3, ry - 1.5);
    g.stroke();
    // Marks round the rim, like the stones of a ring.
    g.fillStyle = 'rgba(244,208,140,.85)';
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      g.fillRect(Math.round(cx + Math.cos(a) * (rx + 4)) - 1, Math.round(cy + Math.sin(a) * (ry + 2)) - 1, 3, 2);
    }
    // The middle: a small cross where the two began.
    g.strokeStyle = 'rgba(244,208,140,.35)';
    g.beginPath();
    g.moveTo(cx - 10, cy);
    g.lineTo(cx + 10, cy);
    g.moveTo(cx, cy - 5);
    g.lineTo(cx, cy + 5);
    g.stroke();
    g.restore();
  },
});

/**
 * What the other fighter is doing that the fight turns on, from their body: its colour, its words, how far it has run
 * (0-1). The colour says the answer: red = a ท่าทำลาย (a guard is the wrong answer), orange / blue / yellow = a flurry,
 * a gap-closer, a heavy blow or a wheel (guard it or step aside), violet = a power move (it cannot be guarded: step aside).
 */
export function tellOf(
  foe: { cast: { kind: string; t: number; windup: number; skillId?: string } | null; stunT: number; blocking: boolean },
  stance = 0,
  broken = 0,
  recov = 0,
) {
  const c = foe.cast;
  if (c && (c.kind === 'break' || c.kind === 'power' || c.kind === 'attack') && c.windup > 0.25) {
    const k = Math.min(1, c.t / Math.max(0.01, c.windup));
    if (c.kind === 'break') return { col: '255,70,60', word: 'ท่าทำลาย!', k };
    if (c.kind === 'power') return { col: '200,110,255', word: 'ท่าพลัง!', k };
    const sk = c.skillId ? getSkill(c.skillId) : null;
    // A lock move (ท่าล็อกเป้า): the gold word, before the flurry/sweep words.
    if (sk?.lockForm) return { col: '255,205,70', word: 'ท่าล็อก!', k };
    if (sk?.dash) return { col: '90,170,255', word: 'พุ่งเข้า!', k };
    if ((sk?.hits ?? 1) >= 2) return { col: '255,150,60', word: 'ท่ารัว!', k };
    if (sk?.aoe) return { col: '255,150,60', word: 'ท่ากวาด!', k };
    return { col: '255,200,80', word: 'ท่าหนัก!', k };
  }
  if (stance > 0) return { col: '255,214,110', word: 'ท่าสวนกลับ!', k: 1 };
  if (broken > 0) return { col: '126,224,178', word: 'การ์ดแตก', k: 1 };
  if (foe.stunT > 0.15) return { col: '126,224,178', word: 'เสียหลัก', k: 1 };
  if (recov > 0.05) return { col: '120,220,255', word: 'ช่องว่าง!', k: 1 };
  if (foe.blocking) return { col: '246,196,108', word: 'ตั้งรับ', k: 1 };
  return null;
}

// The tell at the other fighter's feet: a ring that fills as their wind-up runs out (red for a break, violet for a
// power move), green when they are staggered, amber while they guard.
registerRenderPass({
  id: 'duelTell',
  layer: 'ground',
  order: 226,
  draw({ g, world: w, iso, ox, oy }) {
    if (!duelRing.on || duelRing.foe < 0) return;
    const foe = w.get(duelRing.foe);
    if (!foe || foe.dead) return;
    const tell = tellOf(foe, duelRing.stance[1], duelRing.broken[1], duelRing.recov[1]);
    if (!tell) return;
    const [x, y] = iso.proj(foe.x, foe.y);
    const cx = x + ox;
    const cy = y + oy;
    const pulse = 0.6 + 0.4 * Math.sin(duelRing.t * 14);
    const r = 1.35 * 22.6;
    g.save();
    g.fillStyle = `rgba(${tell.col},${(0.14 + 0.2 * tell.k) * pulse})`;
    ellipse(g, cx, cy, r * tell.k, (r * tell.k) / 2);
    g.fill();
    g.lineWidth = 2;
    g.strokeStyle = `rgba(${tell.col},${0.55 + 0.4 * pulse})`;
    ellipse(g, cx, cy, r, r / 2);
    g.stroke();
    g.restore();
  },
});

// Over each fighter's head, on top of every roof: a marker (blue: you, red: the other), and over the other the word of
// what they are doing. A fighter behind a building is still found, and the tell is read at the foe, not only at the top.
registerRenderPass({
  id: 'duelMarks',
  layer: 'labels',
  order: 110,
  draw({ mg, world: w, px: PX, iso, ox, oy }) {
    if (!duelRing.on) {
      // No bout: the hub rival has a marker over his head so he can be found (click him, right-click his face).
      const r = duelRing.rival >= 0 ? w.get(duelRing.rival) : undefined;
      if (!r) return;
      const [sx, sy] = iso.proj(r.x, r.y);
      const x = (sx + ox) * PX;
      const y = globeBend(sx + ox, sy + oy - 54 * r.scale)[1] * PX;
      const bob = Math.sin(duelRing.t * 4) * 3;
      mg.save();
      mg.textAlign = 'center';
      mg.lineJoin = 'round';
      mg.fillStyle = '#f4d08c';
      mg.strokeStyle = 'rgba(12,9,14,.95)';
      mg.lineWidth = 3;
      mg.beginPath();
      mg.moveTo(x, y - 6 + bob);
      mg.lineTo(x + 8, y - 16 + bob);
      mg.lineTo(x, y - 26 + bob);
      mg.lineTo(x - 8, y - 16 + bob);
      mg.closePath();
      mg.stroke();
      mg.fill();
      if (duelRing.rivalNear) {
        mg.font = `700 14px ${UI}`;
        mg.lineWidth = 4;
        mg.strokeText('ท้าประลอง', x, y - 32 + bob);
        mg.fillText('ท้าประลอง', x, y - 32 + bob);
      }
      mg.restore();
      return;
    }
    mg.save();
    mg.textAlign = 'center';
    mg.lineJoin = 'round';
    for (const [id, mine] of [
      [duelRing.me, true],
      [duelRing.foe, false],
    ] as const) {
      const e = w.get(id);
      if (!e || e.dead) continue;
      const [sx, sy] = iso.proj(e.x, e.y);
      const x = (sx + ox) * PX;
      const y = globeBend(sx + ox, sy + oy - 50 * e.scale)[1] * PX;
      const bob = Math.sin(duelRing.t * 5) * 2;
      mg.fillStyle = mine ? '#6fb4ff' : '#ff6a5a';
      mg.strokeStyle = 'rgba(12,9,14,.95)';
      mg.lineWidth = 3;
      mg.beginPath();
      mg.moveTo(x - 8, y - 14 + bob);
      mg.lineTo(x + 8, y - 14 + bob);
      mg.lineTo(x, y - 2 + bob);
      mg.closePath();
      mg.stroke();
      mg.fill();
      if (mine && (duelRing.stance[0] > 0 || duelRing.broken[0] > 0)) {
        const word = duelRing.stance[0] > 0 ? 'ท่าสวนกลับ' : 'การ์ดแตก';
        mg.font = `700 15px ${UI}`;
        mg.lineWidth = 4;
        mg.strokeStyle = 'rgba(12,9,14,.95)';
        mg.strokeText(word, x, y - 22 + bob);
        mg.fillStyle = duelRing.stance[0] > 0 ? '#ffd66e' : '#b4b0a6';
        mg.fillText(word, x, y - 22 + bob);
      }
      if (!mine) {
        const tell = tellOf(e, duelRing.stance[1], duelRing.broken[1], duelRing.recov[1]);
        // Guarding reads as a small shield over the head (passes/guardReadTell.ts), not a word.
        if (tell && tell.word !== 'ตั้งรับ') {
          mg.font = `700 17px ${UI}`;
          mg.lineWidth = 4;
          mg.strokeStyle = 'rgba(12,9,14,.95)';
          mg.strokeText(tell.word, x, y - 22 + bob);
          mg.fillStyle = `rgb(${tell.col})`;
          mg.fillText(tell.word, x, y - 22 + bob);
        }
      }
    }
    mg.restore();
  },
});

// Your own counter stance (ท่าสวนกลับ): a gold ring that turns at your feet while the window is open, and a broken guard
// shown as grey shards. (The other's are the tell above.)
registerRenderPass({
  id: 'duelStance',
  layer: 'ground',
  order: 227,
  draw({ g, world: w, iso, ox, oy }) {
    if (!duelRing.on) return;
    for (const [id, st, br] of [
      [duelRing.me, duelRing.stance[0], duelRing.broken[0]],
      [duelRing.foe, duelRing.stance[1], duelRing.broken[1]],
    ] as const) {
      const e = w.get(id);
      if (!e || e.dead || (st <= 0 && br <= 0)) continue;
      const [x, y] = iso.proj(e.x, e.y);
      const cx = x + ox;
      const cy = y + oy;
      g.save();
      if (st > 0) {
        const r = 1.1 * 22.6;
        g.lineWidth = 2;
        g.strokeStyle = `rgba(255,214,110,${0.6 + 0.35 * Math.sin(duelRing.t * 10)})`;
        ellipse(g, cx, cy, r, r / 2);
        g.stroke();
        g.fillStyle = 'rgba(255,230,150,.95)';
        for (let i = 0; i < 6; i++) {
          const a = duelRing.t * 3 + (i / 6) * Math.PI * 2;
          g.fillRect(Math.round(cx + Math.cos(a) * r) - 1, Math.round(cy + Math.sin(a) * (r / 2)) - 1, 3, 3);
        }
      }
      if (br > 0) {
        g.fillStyle = 'rgba(180,176,166,.9)';
        for (let i = 0; i < 7; i++) {
          const a = (i / 7) * Math.PI * 2 + 0.4;
          g.fillRect(Math.round(cx + Math.cos(a) * 14), Math.round(cy + Math.sin(a) * 7) - 1, 3, 2);
        }
      }
      g.restore();
    }
  },
});
